import { Link } from "react-router-dom";
import { Star, Calendar, Users, Sparkles, MapPin, Info, Megaphone } from "lucide-react";
import {
  CONFERENCE,
  LUNCH_SPONSORS,
  BCS_LOGO,
  HERO_BG_URL,
} from "../data/conferenceData";

const ANNOUNCEMENTS = [
  {
    title: "Check-in opens at 7:30 AM",
    body: "Come early to Wenonah High School and enjoy a morning of line dancing, scrolling, and exercise. The first 100 to arrive between 7:30 am and 8:00 am will be entered into a drawing for a cash prize.",
  },
  {
    title: "Bring your charged device",
    body: "All sessions are hands-on with AI tools — an iPad, tablet or laptop is needed if you have one.",
  },
  {
    title: "Earn 12 CEUs for the Nurture. Guide. Empower. Conference",
    body: "To earn 12 CEUs, register via PowerSchool PD (344824) and complete all assignments (Pre Lessons, Sessions, Post Lessons) in Schoology. To enroll in the Schoology course, use the access code KVS8-SKXP-JTMHH to enroll.",
  },
  {
    title: "Do not forget to access your book",
    body: "Expand your learning by reading Artificial Intelligence in Education. A copy of this book has been loaded in Schoology under Post Lessons. You will need to read the book to complete your Post Lessons.",
  },
  {
    title: "Need assistance during the conference",
    body: "Find a person in an orange vest.",
  },
];

const quickActions = [
  { label: "Agenda", icon: Calendar, route: "/agenda", color: "var(--primary)", textColor: "white" },
  { label: "My Group", icon: Users, route: "/group", color: "var(--gold)", textColor: "white" },
  { label: "Sessions", icon: Sparkles, route: "/sessions", color: "var(--maroon)", textColor: "white" },
  { label: "Locations", icon: MapPin, route: "/locations", color: "var(--primary-dark)", textColor: "white" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO — full-width ImageBackground with overlay, bottom rounded corners (28px) */}
      <section
        className="relative w-full overflow-hidden"
        style={{
          minHeight: "360px",
          borderBottomLeftRadius: "28px",
          borderBottomRightRadius: "28px",
          backgroundImage: `url(${HERO_BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="home-hero"
      >
        <div
          className="relative w-full h-full flex flex-col justify-end"
          style={{
            minHeight: "360px",
            background: "rgba(9, 41, 54, 0.78)",
            padding: "32px 24px",
            borderBottomLeftRadius: "28px",
            borderBottomRightRadius: "28px",
          }}
        >
          {/* BCS logo: top-right, 72x72, white bg, 16px radius, 6px padding */}
          <div
            className="absolute"
            style={{
              top: "16px",
              right: "16px",
              width: "72px",
              height: "72px",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              borderRadius: "16px",
              padding: "6px",
            }}
            data-testid="bcs-logo"
          >
            <img
              src={BCS_LOGO}
              alt="BCS"
              className="w-full h-full object-contain"
            />
          </div>

          {/* Gold pill with star — alignSelf flex-start, mb 16 */}
          <Link
            to="/success-starts-here"
            data-testid="home-pill-success"
            className="inline-flex items-center self-start"
            style={{
              backgroundColor: "var(--gold)",
              paddingLeft: "12px",
              paddingRight: "12px",
              paddingTop: "6px",
              paddingBottom: "6px",
              borderRadius: "999px",
              gap: "6px",
              marginBottom: "16px",
            }}
          >
            <Star size={14} fill="var(--primary-dark)" strokeWidth={0} />
            <span
              style={{
                color: "var(--primary-dark)",
                fontWeight: 800,
                fontSize: "12px",
                letterSpacing: "1px",
              }}
            >
              Success Starts Here
            </span>
          </Link>

          {/* Hero title — 44px weight 800, line-height 48, mb 8 */}
          <h1
            style={{
              color: "white",
              fontSize: "44px",
              fontWeight: 800,
              lineHeight: "48px",
              marginBottom: "8px",
            }}
          >
            Nurture.
            <br />
            Guide.
            <br />
            Empower.
          </h1>

          {/* Theme line — 16px white weight 700 mb 12 */}
          <p
            style={{
              color: "white",
              fontSize: "16px",
              fontWeight: 700,
              marginBottom: "12px",
            }}
          >
            {CONFERENCE.subTagline}
          </p>

          {/* Welcome — 15px white 92% line-height 22 */}
          <p
            style={{
              color: "rgba(255, 255, 255, 0.92)",
              fontSize: "15px",
              lineHeight: "22px",
            }}
          >
            {CONFERENCE.welcome}
          </p>
        </div>
      </section>

      {/* Lunch sponsor card */}
      <div style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "24px" }}>
        <div
          className="flex flex-col items-center"
          style={{
            backgroundColor: "var(--surface)",
            borderRadius: "16px",
            paddingTop: "16px",
            paddingBottom: "16px",
            paddingLeft: "12px",
            paddingRight: "12px",
            border: "1px solid var(--border)",
          }}
          data-testid="home-sponsor-card"
        >
          <p
            style={{
              fontSize: "11px",
              fontWeight: 800,
              letterSpacing: "1.5px",
              color: "var(--gold)",
              marginBottom: "4px",
              textAlign: "center",
            }}
          >
            LUNCH SPONSOR
          </p>
          <p
            style={{
              fontSize: "14px",
              fontWeight: 700,
              color: "var(--primary-dark)",
              lineHeight: "18px",
              textAlign: "center",
            }}
          >
            Lunch is sponsored by
            <br />
            Imagine Learning &amp; Greene Education Services
          </p>
          <div
            className="grid grid-cols-2 gap-3 mt-3 w-full items-center"
            style={{ maxWidth: "320px" }}
          >
            {LUNCH_SPONSORS.map((s) => (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`home-sponsor-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center justify-center"
                style={{ height: "60px" }}
              >
                <img
                  src={s.logo}
                  alt={s.name}
                  style={{
                    maxHeight: "56px",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Access — 2x2 grid with icons, 18 radius, minHeight 100, padding 16 */}
      <div style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "24px" }}>
        <p
          style={{
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "1.5px",
            color: "var(--primary)",
            textTransform: "uppercase",
            marginBottom: "12px",
          }}
        >
          Quick Access
        </p>
        <div className="flex flex-wrap" style={{ gap: "12px" }}>
          {quickActions.map((a) => {
            const Icon = a.icon;
            return (
              <Link
                key={a.label}
                to={a.route}
                data-testid={`quick-${a.label.toLowerCase().replace(" ", "-")}`}
                className="flex flex-col justify-between shadow-tile"
                style={{
                  width: "calc(50% - 6px)",
                  minHeight: "100px",
                  borderRadius: "18px",
                  padding: "16px",
                  backgroundColor: a.color,
                  color: a.textColor,
                }}
              >
                <Icon size={28} color={a.textColor} strokeWidth={1.8} />
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    marginTop: "8px",
                    color: a.textColor,
                  }}
                >
                  {a.label}
                </p>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Info card — info icon + title + body */}
      <div style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "24px" }}>
        <div
          className="flex items-start"
          style={{
            backgroundColor: "var(--surface)",
            borderRadius: "16px",
            padding: "16px",
            border: "1px solid var(--border)",
          }}
          data-testid="home-event-info"
        >
          <Info size={22} color="var(--primary)" style={{ flexShrink: 0 }} />
          <div className="flex-1" style={{ marginLeft: "10px" }}>
            <p
              style={{
                fontWeight: 700,
                color: "var(--primary-dark)",
                fontSize: "14px",
                marginBottom: "4px",
              }}
            >
              District-Wide Professional Development
            </p>
            <p
              style={{
                fontWeight: 700,
                color: "var(--primary-dark)",
                fontSize: "14px",
                marginBottom: "4px",
              }}
            >
              {CONFERENCE.date}
            </p>
            <p
              style={{
                color: "var(--text-muted)",
                fontSize: "13px",
                lineHeight: "19px",
              }}
            >
              A full day of learning, collaboration, and discovery — designed
              for the educators who guide our K-2 learners.
            </p>
          </div>
        </div>
      </div>

      {ANNOUNCEMENTS.length > 0 && (
        <div
          style={{ paddingLeft: "20px", paddingRight: "20px", marginTop: "24px" }}
          data-testid="announcements-section"
        >
          <div
            className="flex items-center"
            style={{ gap: "8px", marginBottom: "12px" }}
          >
            <Megaphone size={18} color="var(--primary)" strokeWidth={2.2} />
            <p
              style={{
                fontSize: "12px",
                fontWeight: 800,
                letterSpacing: "1.8px",
                color: "var(--primary)",
                textTransform: "uppercase",
              }}
            >
              Announcements
            </p>
          </div>
          <div>
            {ANNOUNCEMENTS.map((a) => (
              <div
                key={a.title}
                data-testid={`announcement-${a.title}`}
                style={{
                  backgroundColor: "var(--surface)",
                  borderRadius: "16px",
                  paddingTop: "14px",
                  paddingBottom: "14px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  marginBottom: "10px",
                  border: "1px solid rgba(20,82,97,0.15)",
                  borderLeft: "4px solid var(--gold)",
                }}
              >
                <h3
                  style={{
                    fontWeight: 700,
                    color: "var(--primary-dark)",
                    fontSize: "15px",
                    lineHeight: "20px",
                  }}
                >
                  {a.title}
                </h3>
                {a.body && (
                  <p
                    style={{
                      marginTop: "4px",
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: "var(--text-muted)",
                    }}
                  >
                    {a.body}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ height: "24px" }} />
    </div>
  );
}
